import React, { useEffect, useState } from 'react';
import { Button, Col, DatePicker, Divider, Input, InputNumber, Row, Space, Tag, Typography, Upload, message } from 'antd';
import dayjs from 'dayjs';
import { DeleteOutlined, PlusOutlined, UploadOutlined } from '@ant-design/icons';
import { useI18n } from '../i18n';
import { computeTotalLeadTime, getSupplierTypeFields, getSupplierTypeRequiredFields } from '../utils/quoteHelpers';
import RichTextEditor from './RichTextEditor';
import AttachmentPreview from './AttachmentPreview';

const { Text } = Typography;
const { TextArea } = Input;

let lineIdCounter = 0;
const nextLineId = () => {
  lineIdCounter += 1;
  return `line-${Date.now()}-${lineIdCounter}`;
};

const buildLine = (data, requestedQty, role) => ({
  id: data?.id || nextLineId(),
  role,
  quantity: data?.quantity ?? requestedQty ?? null,
  productDetails: data?.productDetails || '',
  unitPrice: data?.unitPrice ?? null,
  brandingDecoration: data?.brandingDecoration || '',
  productionTime: data?.productionTime ?? null,
  shippingTime: data?.shippingTime ?? null,
  shippingCost: data?.shippingCost ?? null,
  validUntil: data?.validUntil ? dayjs(data.validUntil) : null,
  files: data?.files || [],
  comments: data?.comments || '',
});

// A line's role, falling back to the supplier's sole role for pre-role-tag
// lines (Quotes created before this role split existed).
const lineRole = (line, roles) => line.role || (roles.length === 1 ? roles[0] : null);

const buildLinesForRole = (data, requestedQty, role, roles) => {
  const roleLines = (data?.lines || []).filter((line) => lineRole(line, roles) === role);
  return roleLines.length > 0
    ? roleLines.map((line) => buildLine(line, requestedQty, role))
    : [buildLine(null, requestedQty, role)];
};

const buildAllLines = (data, requestedQty, roles) => roles.flatMap((role) => buildLinesForRole(data, requestedQty, role, roles));

/**
 * Quote cost-entry form rendered inside the Supplier Quote edit modal.
 * A supplier can hold more than one role on this Quote (Manufacturing,
 * Services, Logistics — from the Selection_Wizard); each role gets its own
 * independently editable set of cost-entry lines (e.g. different quantity
 * breaks) with the fields relevant to that role — see `getSupplierTypeFields`.
 */
export default function SupplierQuoteFormPanel({
  supplierId,
  data,
  quantity: requestedQty = 0,
  productId = null,
  roles = ['Manufacturing'],
  certificates = [],
  onSave,
  onCancel,
}) {
  const { t } = useI18n();
  const [lines, setLines] = useState(() => buildAllLines(data, requestedQty, roles));
  const [errorsByLine, setErrorsByLine] = useState({});

  const complianceCertificates = certificates.filter((cert) => (
    String(cert.supplierId) === String(supplierId) && (cert.productId == null || cert.productId === productId)
  ));

  useEffect(() => {
    setLines(buildAllLines(data, requestedQty, roles));
    setErrorsByLine({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [supplierId, data]);

  const updateLine = (lineId, field, value) => setLines((prev) => prev.map((line) => (
    line.id === lineId ? { ...line, [field]: value } : line
  )));

  const addLine = (role) => setLines((prev) => [...prev, buildLine(null, requestedQty, role)]);
  const removeLine = (lineId) => setLines((prev) => prev.filter((line) => line.id !== lineId));

  const fieldLabelByName = {
    unitPrice: t('Unit Price'),
    productionTime: t('Production Time'),
    shippingTime: t('Shipping Time'),
    shippingCost: t('Shipping Cost'),
  };

  const handleSave = () => {
    const nextErrorsByLine = {};
    lines.forEach((line) => {
      const lineErrors = {};
      getSupplierTypeRequiredFields(line.role).forEach((field) => {
        if (line[field] === null || line[field] === '') {
          lineErrors[field] = t('{{field}} is required', { field: fieldLabelByName[field] || field });
        }
      });
      if (Object.keys(lineErrors).length > 0) nextErrorsByLine[line.id] = lineErrors;
    });

    if (Object.keys(nextErrorsByLine).length > 0) {
      setErrorsByLine(nextErrorsByLine);
      return;
    }
    setErrorsByLine({});
    onSave?.(supplierId, {
      lines: lines.map((line) => ({
        ...line,
        validUntil: line.validUntil ? line.validUntil.format('YYYY-MM-DD') : null,
      })),
    });
    message.success(t('Supplier quote saved'));
  };

  const fieldStyle = { width: '100%' };

  const renderLine = (line, roleLines) => {
    const index = roleLines.indexOf(line);
    const errors = errorsByLine[line.id] || {};
    const requiredFields = getSupplierTypeRequiredFields(line.role);
    const visibleFields = getSupplierTypeFields(line.role);
    const showField = (field) => visibleFields.includes(field);
    const productSubtotal = Number(line.unitPrice || 0) * Number(line.quantity || 0);
    return (
      <div key={line.id} style={{ marginBottom: 16 }}>
        {index > 0 && <Divider style={{ margin: '12px 0' }} />}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <Text strong style={{ fontSize: 13, textTransform: 'uppercase' }}>{t('Proposed Quote')} {index + 1}</Text>
          {roleLines.length > 1 && (
            <Button type="link" danger size="small" icon={<DeleteOutlined />} onClick={() => removeLine(line.id)}>
              {t('Remove line')}
            </Button>
          )}
        </div>

        <Row gutter={[16, 12]}>
          {showField('productDetails') && (
            <Col xs={24} md={12}>
              <Text style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>{t('Product Details')}</Text>
              <RichTextEditor
                value={line.productDetails}
                onChange={(html) => updateLine(line.id, 'productDetails', html)}
                minHeight={70}
              />
            </Col>
          )}
          {showField('brandingDecoration') && (
            <Col xs={24} md={12}>
              <Text style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>{t('Branding / Decoration')}</Text>
              <RichTextEditor
                value={line.brandingDecoration}
                onChange={(html) => updateLine(line.id, 'brandingDecoration', html)}
                minHeight={70}
              />
            </Col>
          )}
        </Row>

        <Row gutter={[16, 12]} style={{ marginTop: 12 }}>
          <Col xs={12} md={4}>
            <Text style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>{t('Quantity')}</Text>
            <InputNumber min={0} style={fieldStyle} value={line.quantity} onChange={(v) => updateLine(line.id, 'quantity', v)} />
          </Col>
          <Col xs={12} md={4}>
            <Text style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>{t('Requested Qty')}</Text>
            <InputNumber disabled style={fieldStyle} value={requestedQty} />
          </Col>
          {showField('unitPrice') && (
            <Col xs={12} md={4}>
              <Text style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>{t('Unit Price')}{requiredFields.includes('unitPrice') ? ' *' : ''}</Text>
              <InputNumber min={0} style={fieldStyle} value={line.unitPrice} onChange={(v) => updateLine(line.id, 'unitPrice', v)} status={errors.unitPrice ? 'error' : undefined} />
              {errors.unitPrice && <Text type="danger" style={{ fontSize: 12 }}>{errors.unitPrice}</Text>}
            </Col>
          )}
          {showField('productionTime') && (
            <Col xs={12} md={4}>
              <Text style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>{t('Production Time')}{requiredFields.includes('productionTime') ? ' *' : ''}</Text>
              <InputNumber min={0} max={999} style={fieldStyle} value={line.productionTime} onChange={(v) => updateLine(line.id, 'productionTime', v)} status={errors.productionTime ? 'error' : undefined} />
              {errors.productionTime && <Text type="danger" style={{ fontSize: 12 }}>{errors.productionTime}</Text>}
            </Col>
          )}
          {showField('unitPrice') && (
            <Col xs={12} md={4}>
              <Text style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>{t('Product Subtotal')}</Text>
              <InputNumber disabled style={fieldStyle} value={productSubtotal} />
            </Col>
          )}
          {showField('shippingCost') && (
            <Col xs={12} md={4}>
              <Text style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>{t('Shipping Cost')}{requiredFields.includes('shippingCost') ? ' *' : ''}</Text>
              <InputNumber min={0} style={fieldStyle} value={line.shippingCost} onChange={(v) => updateLine(line.id, 'shippingCost', v)} status={errors.shippingCost ? 'error' : undefined} />
              {errors.shippingCost && <Text type="danger" style={{ fontSize: 12 }}>{errors.shippingCost}</Text>}
            </Col>
          )}
          {showField('validUntil') && (
            <Col xs={12} md={4}>
              <Text style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>{t('Valid Until')}</Text>
              <DatePicker style={fieldStyle} value={line.validUntil} onChange={(v) => updateLine(line.id, 'validUntil', v)} format="YYYY-MM-DD" />
            </Col>
          )}
          {showField('shippingTime') && (
            <Col xs={12} md={4}>
              <Text style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>{t('Shipping Time')}{requiredFields.includes('shippingTime') ? ' *' : ''}</Text>
              <InputNumber min={0} max={999} style={fieldStyle} value={line.shippingTime} onChange={(v) => updateLine(line.id, 'shippingTime', v)} status={errors.shippingTime ? 'error' : undefined} />
              {errors.shippingTime && <Text type="danger" style={{ fontSize: 12 }}>{errors.shippingTime}</Text>}
            </Col>
          )}
          {showField('productionTime') && showField('shippingTime') && (
            <Col xs={12} md={4}>
              <Text style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>{t('Total Lead Time')}</Text>
              <InputNumber
                disabled
                style={fieldStyle}
                value={computeTotalLeadTime(line)}
                addonAfter={computeTotalLeadTime(line) === 1 ? t('Day') : t('Days')}
              />
            </Col>
          )}
        </Row>

        <Row gutter={[16, 12]} style={{ marginTop: 12 }}>
          <Col xs={24} md={12}>
            <Text style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>{t('Attachment')}</Text>
            {line.files.length > 0 && (
              <div style={{ marginBottom: 8 }}>
                <AttachmentPreview files={line.files} />
              </div>
            )}
            <Upload
              multiple
              showUploadList={false}
              beforeUpload={(file) => {
                updateLine(line.id, 'files', [
                  ...line.files,
                  { uid: `${Date.now()}-${file.name}`, name: file.name, status: 'done', originFileObj: file },
                ]);
                return false;
              }}
            >
              <Button size="small" icon={<UploadOutlined />}>{t('Attach files')}</Button>
            </Upload>
            {line.files.length > 0 && (
              <Space wrap size={4} style={{ marginTop: 8 }}>
                {line.files.map((file) => (
                  <Tag
                    key={file.uid}
                    closable
                    onClose={() => updateLine(line.id, 'files', line.files.filter((f) => f.uid !== file.uid))}
                  >
                    {file.name}
                  </Tag>
                ))}
              </Space>
            )}
          </Col>
          <Col xs={24} md={12}>
            <Text style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>{t('Comments')}</Text>
            <TextArea rows={3} value={line.comments} onChange={(e) => updateLine(line.id, 'comments', e.target.value)} />
          </Col>
        </Row>
      </div>
    );
  };

  return (
    <div>
      {roles.map((role) => {
        const roleLines = lines.filter((line) => line.role === role);
        return (
          <div key={role} style={{ marginBottom: 20 }}>
            {roles.length > 1 && (
              <Tag style={{ marginBottom: 12, fontSize: 12 }} color="processing">{t(role)}</Tag>
            )}
            {roleLines.map((line) => renderLine(line, roleLines))}
            <Button type="dashed" icon={<PlusOutlined />} onClick={() => addLine(role)} style={{ marginBottom: 8 }}>
              {t('Add line')}
            </Button>
          </div>
        );
      })}

      <div style={{ marginTop: 8 }}>
        <Text style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>{t('Compliance Info')}</Text>
        {complianceCertificates.length > 0 ? (
          <Space wrap size={4}>
            {complianceCertificates.map((cert) => (
              <Tag key={cert.id} style={{ fontSize: 11, margin: 0 }}>{cert.name}</Tag>
            ))}
          </Space>
        ) : (
          <Text type="secondary" style={{ fontSize: 12 }}>{t('No certificates on file for this supplier')}</Text>
        )}
      </div>

      <Space style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
        {onCancel && <Button onClick={onCancel}>{t('Cancel')}</Button>}
        <Button type="primary" onClick={handleSave}>{t('Save')}</Button>
      </Space>
    </div>
  );
}
